import { ArrowsClockwiseIcon } from "@phosphor-icons/react/dist/csr/ArrowsClockwise";
import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import { Formik, FormikProps, FormikState } from "formik";
import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Alert, AlertDescription } from "@volca/ui/components/ui/alert";
import { Button } from "@volca/ui/components/ui/button";
import { Checkbox } from "@volca/ui/components/ui/checkbox";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { SERVICE_TITAN_STATUSES } from "@/business/constants";
import BusinessInfoPair from "@/business-settings/business/business-info-pair";
import {
  useServiceTitanIntegration,
  ServiceTitanFormValues,
} from "./useServiceTitanIntegration";

interface BusinessServiceTitanSettingsProps {
  isEditMode: boolean;
  setIsEditMode: (isEditMode: boolean) => void;
}

const BusinessServiceTitanSettings = ({
  isEditMode,
  setIsEditMode,
}: BusinessServiceTitanSettingsProps) => {
  const formikRef = useRef<FormikProps<ServiceTitanFormValues>>(null);
  const { businessId } = useParams();
  const [enableIntegration, setEnableIntegration] = useState(true);

  const {
    status,
    config,
    initialValues,
    validationSchema,
    isPending: isUpsertingCredentials,
    handleSubmit: handleSubmitCredentials,
    showTenantIdField,
    showClientFields,
    showErrorState,
    isSubmitDisabled,
  } = useServiceTitanIntegration({ businessId });

  useEffect(() => {
    formikRef.current?.resetForm({ values: initialValues });
    setEnableIntegration(true);
  }, [isEditMode, initialValues]);

  const handleSubmit = async (
    values: ServiceTitanFormValues,
    {
      setSubmitting,
      resetForm,
    }: {
      setSubmitting: (isSubmitting: boolean) => void;
      resetForm: (values: Partial<FormikState<ServiceTitanFormValues>>) => void;
    },
  ) => {
    const onSuccess = (resp: { data?: { credentials_status?: string } }) => {
      const nextStatus = resp.data?.credentials_status;
      const isComplete =
        nextStatus === SERVICE_TITAN_STATUSES.connection_established;

      if (isComplete) {
        resetForm({ values });
        setIsEditMode(false);
      } else {
        resetForm({ values });
      }
    };

    try {
      handleSubmitCredentials(values);
      onSuccess({ data: { credentials_status: status } });
    } catch (error) {
      console.error("Error updating ServiceTitan credentials:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack className="w-full" align="start" gap={4}>
      <Text variant="title">ServiceTitan integration</Text>
      <Stack className="w-full" align="start" gap={4}>
        {showErrorState ? (
          <Alert color="danger" className="max-w-lg" aria-live="assertive">
            <WarningIcon />
            <AlertDescription>
              We were unable to connect to your ServiceTitan. Please check that
              all values are correct and submit again.
            </AlertDescription>
          </Alert>
        ) : (
          !!config?.subtitle && (
            <Stack className="w-full" align="start" gap={4}>
              <Text>{config?.subtitle}</Text>
            </Stack>
          )
        )}
        {!!status && (
          <Formik
            innerRef={formikRef}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
            fullWidth
          >
            {({
              values,
              handleChange,
              dirty,
              isValid,
              isSubmitting,
              setFieldValue,
              setSubmitting,
              resetForm,
            }) => (
              <>
                {(showTenantIdField ||
                  showClientFields ||
                  (config?.showSubmitButton && isEditMode)) && (
                  <Stack
                    direction="column"
                    gap={6}
                    align="start"
                    className="w-full 2xl:flex-row 2xl:items-center"
                  >
                    {showTenantIdField && (
                      <BusinessInfoPair
                        isEditMode={isEditMode}
                        label="Tenant ID"
                        value={values.tenant_id}
                        onChange={handleChange}
                        name="tenant_id"
                        placeholder="Enter tenant ID"
                      />
                    )}
                    {showClientFields && (
                      <>
                        <BusinessInfoPair
                          isEditMode={isEditMode}
                          label="Client ID"
                          value={values.client_id}
                          onChange={handleChange}
                          name="client_id"
                          placeholder="Enter client ID"
                        />
                        <BusinessInfoPair
                          isEditMode={isEditMode}
                          label="Client secret"
                          value={values.client_secret}
                          onChange={handleChange}
                          name="client_secret"
                          placeholder="Enter client secret"
                          className="w-full sm:size-1/3"
                        />
                      </>
                    )}
                    {config?.showSubmitButton && isEditMode && (
                      <Button
                        size="sm"
                        type="button"
                        onClick={() => {
                          handleSubmit(values, { setSubmitting, resetForm });
                        }}
                        disabled={
                          isSubmitDisabled(values) ||
                          !dirty ||
                          !isValid ||
                          isUpsertingCredentials ||
                          isSubmitting
                        }
                        aria-busy={
                          isUpsertingCredentials || isSubmitting || undefined
                        }
                      >
                        {(isUpsertingCredentials || isSubmitting) && (
                          <Spinner aria-hidden data-icon="inline-start" />
                        )}
                        Submit
                      </Button>
                    )}
                  </Stack>
                )}

                {status === SERVICE_TITAN_STATUSES.tenant_id_received && (
                  <Stack className="w-full" align="start" gap={4}>
                    <Text tone="muted">
                      We received your Tenant ID. An email with next steps will
                      be sent soon.
                    </Text>
                  </Stack>
                )}

                {isUpsertingCredentials && (
                  <Stack direction="row" gap={2} align="center">
                    <ArrowsClockwiseIcon className="h-4 w-4" />
                    <Text tone="muted">Connecting to ServiceTitan…</Text>
                  </Stack>
                )}

                {status === SERVICE_TITAN_STATUSES.connection_established &&
                  isEditMode && (
                    <Stack direction="row" gap={2} align="center">
                      <Checkbox
                        checked={enableIntegration}
                        onCheckedChange={() => {
                          setEnableIntegration(!enableIntegration);
                          setFieldValue("client_id", "");
                          setFieldValue("client_secret", "");
                        }}
                      />
                      <Text>Integration enabled</Text>
                    </Stack>
                  )}
              </>
            )}
          </Formik>
        )}
      </Stack>
    </Stack>
  );
};

export default BusinessServiceTitanSettings;
